/**
 * Aides contextuelles fiche produit — Lot UI-A.
 * Textes modifiables sans toucher au JSX des sections.
 */

import { PRODUCT_SECTION_KEYS as S } from './produitSectionKeys.js';

/** @typedef {'subtitle' | 'popover' | 'both'} HelpTier */

/**
 * @typedef {Object} SectionHelpEntry
 * @property {HelpTier} tier
 * @property {string} [subtitle]
 * @property {string} [popover]
 * @property {string} [popoverTitle]
 */

/** @type {Record<string, SectionHelpEntry>} */
export const PRODUIT_SECTION_HELP = Object.freeze({
  [S.PHOTOS]: {
    tier: 'subtitle',
    subtitle: 'La première photo est celle affichée en couverture sur votre menu en ligne.',
  },
  [S.INFOS]: {
    tier: 'subtitle',
    subtitle: 'Le nom apparaît tel quel sur le menu client ; la description est optionnelle mais conseillée.',
  },
  [S.PRIX]: {
    tier: 'subtitle',
    subtitle: 'Prix TTC affiché au client pour ce produit (hors suppléments ajoutés via les options).',
  },
  prixPromo: {
    tier: 'popover',
    popoverTitle: 'Prix promo',
    popover: 'Optionnel — affiche un prix promotionnel barré à côté du prix normal.',
  },
  tva: {
    tier: 'subtitle',
    subtitle: 'Taux appliqué au prix TTC ; 5,5 % pour la restauration selon votre activité.',
  },
  [S.CATEGORIE]: {
    tier: 'subtitle',
    subtitle: 'Classe le produit dans votre carte (entrées, pizzas, boissons…) pour faciliter la navigation.',
  },
  [S.STOCK]: {
    tier: 'subtitle',
    subtitle: 'Quantité limitée ou illimité si vous ne suivez pas le stock sur ce produit.',
  },
  [S.OPTIONS]: {
    tier: 'popover',
    popoverTitle: 'Options & variantes',
    popover:
      'Permet d\'ajouter des choix au produit : cuisson, taille, boisson, suppléments payants, menus personnalisés, etc. Exemples : burger « à point », pizza « grande », boisson en +2 €. Laissez vide si le produit n\'a aucun choix à proposer.',
  },
  [S.BADGES]: {
    tier: 'popover',
    popoverTitle: 'Caractéristiques',
    popover:
      'Bio, vegan, local et les autres mentions se gèrent dans Caractéristiques. Ajoutez simplement la mention souhaitée ; elle reste synchronisée pour l’affichage client. Les allergènes restent dans Ingrédients & allergènes.',
  },
  [S.INGREDIENTS]: {
    tier: 'popover',
    popoverTitle: 'Ingrédients & allergènes',
    popover:
      'Liez les ingrédients de votre catalogue pour l\'affichage client, les allergènes et la gestion des ruptures. Si un ingrédient est en rupture, les produits qui l\'utilisent peuvent être impactés.',
  },
  casParticuliers: {
    tier: 'both',
    subtitle: 'Réservé aux produits spéciaux : plateau sushi, planche traiteur, vente au poids.',
    popoverTitle: 'Cas particuliers',
    popover:
      'Activez ici uniquement si votre produit ne se vend pas comme un article simple. Un burger, une pizza ou une boisson n\'a en général besoin que du prix et des options.',
  },
  [S.VENTE_AU_POIDS]: {
    tier: 'both',
    subtitle: 'Pour les produits vendus au kilo, à la coupe ou au poids variable.',
    popoverTitle: 'Vente au poids',
    popover:
      'Charcuterie, fromage à la coupe, produits au kg… Le client paie selon le poids commandé. Ne pas utiliser pour un produit à prix fixe.',
  },
  [S.COMPOSITION_PIECES]: {
    tier: 'both',
    subtitle: 'Pour les plateaux où le client compose lui-même (ex. sushi par pièces).',
    popoverTitle: 'Plateau par pièces',
    popover:
      'Le client compose un plateau selon vos règles (nombre de pièces, tailles 8/14/18…). Exemple : « Composer votre plateau ». Différent d\'une planche traiteur au nombre de personnes.',
  },
  [S.ELEMENTS_COMPOSABLES]: {
    tier: 'both',
    subtitle: 'Liste des pièces que le client peut choisir pour remplir le plateau.',
    popoverTitle: 'Pièces composables',
    popover:
      'Chaque ligne est un choix possible (maki, sushi, brochette…). Visible si le mode plateau par pièces est activé.',
  },
  [S.PLANCHE_PERSONNES]: {
    tier: 'both',
    subtitle: 'Pour vendre une planche calculée selon un nombre de personnes.',
    popoverTitle: 'Planche par personnes',
    popover:
      'Tarif par personne (minimum et maximum de convives). Exemple : planche charcuterie pour 4 à 10 personnes. Différent du plateau sushi par pièces.',
  },
  [S.LIEN_PLATEAUX]: {
    tier: 'both',
    subtitle: 'Pour un produit proposé sur une planche traiteur existante.',
    popoverTitle: 'Disponible sur les planches',
    popover:
      'Cochez les planches sur lesquelles ce produit peut être utilisé. À utiliser après avoir créé la planche.',
  },
  ingredientsPlanche: {
    tier: 'popover',
    popoverTitle: 'Ingrédients de la planche personnalisable',
    popover:
      'Produits que le client pourra choisir pour composer sa planche. Différent des ingrédients & allergènes du catalogue.',
  },
  taillesPlateau: {
    tier: 'popover',
    popoverTitle: 'Tailles du plateau',
    popover: 'Formats proposés au client (ex. 8, 14, 18 pièces) et prix de chaque taille.',
  },
  [S.TEMPLATES]: {
    tier: 'popover',
    popoverTitle: 'Modèles',
    popover: 'Pré-remplit la fiche à partir d\'un modèle. Ignorez si vous créez le produit manuellement.',
  },
  [S.CHAMPS_METIER]: {
    tier: 'popover',
    popoverTitle: 'Champs spécifiques',
    popover: 'Informations propres à votre type de commerce. À remplir uniquement si nécessaire.',
  },
});

export function getSectionHelp(key) {
  return PRODUIT_SECTION_HELP[key] ?? null;
}
